/**
 * Canonical wallet roles from analysis/label_model.py (LABEL01).
 * The report and the inspector read this list. They do not keep a second vocabulary.
 */
export const WALLET_ROLES = [
  'exchange_hot',
  'exchange_deposit',
  'customer_wallet',
  'card_offramp',
  'issuer',
  'unknown',
] as const;

export type WalletRole = (typeof WALLET_ROLES)[number];

export const WALLET_ROLE_LABEL: Record<string, string> = {
  exchange_hot: 'Hot wallet',
  exchange_deposit: 'Deposit wallet',
  customer_wallet: 'Customer wallet',
  card_offramp: 'Card gateway',
  issuer: 'Issuer',
  // NOTICE08: graph and matrix roles a notice row may carry besides the label vocabulary above
  subject: 'Subject wallet',
  feeder: 'Feeder',
  hub: 'Hub',
  unknown: 'Unknown role',
};

/** ROLE03: the words a row carries when the direction test contradicted its wallet role (label_model.ROLE_CONTRADICTED_TEXT). */
export const ROLE_CONTRADICTED_TEXT = 'role contradicted by direction';

export const LABEL_CLASS_LABEL: Record<string, string> = {
  owner: 'Owner',
  customer_of: 'Customer of',
  inference: 'Inference',
  unknown: 'Unknown',
};

export const CONFIRMATION_LABEL: Record<string, string> = {
  vendor_label: 'Vendor label',
  registry_list: 'Registry list',
  exchange_confirmed: 'Exchange confirmed',
};

export const SOURCE_LABEL: Record<string, string> = {
  registry_list: 'Registry list',
  registry: 'Registry list',
  misttrack: 'MistTrack',
  metasleuth: 'MetaSleuth',
  manual: 'Manual',
  vendor_label: 'Vendor label',
};

export function isCustomerOf(row: { wallet_role?: unknown; label_class?: unknown } | null | undefined): boolean {
  if (!row) return false;
  return String(row.wallet_role || '') === 'customer_wallet' || String(row.label_class || '') === 'customer_of';
}

function text(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

/** "customer of <brand>" when the row is a customer wallet. Null otherwise. */
export function customerFacingName(row: {
  brand?: unknown;
  entity_name?: unknown;
  exchange_name?: unknown;
  label?: unknown;
  wallet_role?: unknown;
  label_class?: unknown;
} | null | undefined): string | null {
  if (!isCustomerOf(row)) return null;
  const who = text(row?.brand) || text(row?.exchange_name) || text(row?.entity_name) || text(row?.label);
  return who ? `customer of ${who}` : 'customer of an unnamed exchange';
}
